import React, { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useQueue } from '../../queue/hooks/useQueue';
// นำเข้าเครื่องมือทำกราฟจาก Recharts
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

export default function HistoryPage({ setActiveTab }) {
  const { user } = useAuth();
  const { jobs, loadingQueue } = useQueue(user);
  const [filter, setFilter] = useState('all');

  // 🚀 1. คัดกรองงานให้ตรงกับสิทธิ์ (ช่างเห็นแค่งานตัวเอง / เจ้าของเห็นทั้งหมด)
  const displayJobs = user?.role === 'owner' ? jobs : jobs.filter(job => {
    const techIds = job.technicianId ? job.technicianId.split(',') : [];
    return techIds.includes(user?.uid);
  });

  // 🚀 2. สูตรนับจำนวนแยกตามสถานะ (แก้บั๊กเลข 12 ซ้ำกัน)
  const counts = {
    all: displayJobs.length,
    in_progress: displayJobs.filter(j => j.status === 'in_progress').length,
    pending: displayJobs.filter(j => j.status === 'pending' || j.status === 'traveling').length,
    completed: displayJobs.filter(j => j.status === 'completed').length,
    cancelled: displayJobs.filter(j => j.status === 'cancelled').length,
  };

  // 🚀 3. กรองงานตามสถานะแท็บที่กดเลือก
  const filteredJobs = displayJobs.filter(job => {
    if (filter === 'all') return true;
    if (filter === 'pending') return job.status === 'pending' || job.status === 'traveling';
    return job.status === filter;
  });

  // 💰 คำนวณรายได้ทั้งหมด (เฉพาะสถานะ จบงาน และเฉพาะ Owner)
  const totalEarnings = jobs
    .filter(job => job.status === 'completed')
    .reduce((sum, job) => sum + Number(job.price || 0), 0);

  // 📊 เตรียมข้อมูลสำหรับทำกราฟแยกรายเดือน (ม.ค. - ธ.ค.)
  const thMonths = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
  
  // สร้างโครงอาร์เรย์รายเดือนเริ่มต้น 12 เดือน ยอดเงินเป็น 0
  const monthlyData = thMonths.map(month => ({ name: month, รายได้: 0 }));

  // นำงานที่เสร็จแล้วมาบวกยอดเข้าในแต่ละเดือนตาม Timestamp (createdAt)
  jobs.forEach(job => {
    if (job.status === 'completed' && job.createdAt) {
      const jobDate = new Date(job.createdAt);
      const monthIndex = jobDate.getMonth(); // จะได้เลข 0-11
      monthlyData[monthIndex].รายได้ += Number(job.price || 0);
    }
  });

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending': return <span className="px-3 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-700">รอเดินทาง</span>;
      case 'traveling': return <span className="px-3 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-700">กำลังเดินทาง</span>;
      case 'in_progress': return <span className="px-3 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-700">กำลังทำ</span>;
      case 'completed': return <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">เสร็จสิ้น</span>;
      case 'cancelled': return <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700">ยกเลิก</span>;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex justify-center">
      <div className="w-full max-w-md bg-gray-50 flex flex-col relative shadow-2xl h-[100dvh] overflow-hidden">
        
        {/* 🚀 ส่วนที่ 1: ส่วนหัวหน้าจอ (แยกตามสิทธิ์ Owner vs ช่าง) */}
        <div className="shrink-0 z-10">
          
          {user?.role === 'owner' ? (
            // แดชบอร์ดของเจ้าของร้าน (เห็นรายได้ + กราฟ)
            <>
              <div className="bg-gray-800 dark:bg-gray-900 text-white px-5 py-6 shadow-md text-center rounded-b-3xl border-b border-gray-700">
                <h1 className="text-lg font-bold mb-1">สรุปภาพรวมรายได้</h1>
                <p className="text-gray-400 text-xs mb-1">รายรับรวม</p>
                <p className="text-4xl font-black text-green-400 mt-2">฿{totalEarnings.toLocaleString()}</p>
              </div>

              {/* กราฟสถิติรายรับ (โชว์เฉพาะเจ้าของ) */}
              <div className="bg-white dark:bg-gray-700 mx-4 mt-4 p-4 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-600">
                <p className="text-xs font-bold text-gray-500 dark:text-gray-300 mb-4">สถิติรายรับแยกตามเดือน (บาท)</p>
                <div className="h-40 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                      <XAxis dataKey="name" tick={{fontSize: 10, fill: '#6b7280'}} axisLine={false} tickLine={false} />
                      <YAxis tick={{fontSize: 10, fill: '#6b7280'}} axisLine={false} tickLine={false} width={35} />
                      <Tooltip cursor={{fill: '#f3f4f6'}} contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'}} />
                      <Bar dataKey="รายได้" fill="#06C755" radius={[4, 4, 0, 0]} barSize={20} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          ) : (
            // ส่วนหัวของช่างทั่วไป (ไม่เห็นเงิน)
            <div className="bg-blue-600 px-6 py-8 text-center text-white rounded-b-3xl shadow-md mb-2">
              <h1 className="text-2xl font-black">ประวัติการทำงาน</h1>
              <p className="text-sm text-blue-200 mt-1">รายการงานซ่อมที่คุณรับผิดชอบ</p>
            </div>
          )}
        </div>

        {/* 🚀 ส่วนที่ 2: รายการพื้นที่ทำงานด้านใน (เลื่อนขึ้นลงได้) */}
        <div className="flex-1 overflow-y-auto pb-24 mt-2">
          
          {/* 🚀 แท็บเมนูกรองสถานะ (ดึงตัวเลขจาก counts มาโชว์) */}
          <div className="flex overflow-x-auto px-4 py-2 gap-2 shrink-0 hide-scrollbar">
            <button onClick={() => setFilter('all')} className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${filter === 'all' ? 'bg-gray-800 text-white' : 'bg-white text-gray-500 border border-gray-200'}`}>ทั้งหมด ({counts.all})</button>
            <button onClick={() => setFilter('in_progress')} className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${filter === 'in_progress' ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 border border-gray-200'}`}>กำลังทำ ({counts.in_progress})</button>
            <button onClick={() => setFilter('pending')} className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${filter === 'pending' ? 'bg-yellow-500 text-white' : 'bg-white text-gray-500 border border-gray-200'}`}>รอเดินทาง ({counts.pending})</button>
            <button onClick={() => setFilter('completed')} className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${filter === 'completed' ? 'bg-green-500 text-white' : 'bg-white text-gray-500 border border-gray-200'}`}>เสร็จสิ้น ({counts.completed})</button>
            <button onClick={() => setFilter('cancelled')} className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-colors ${filter === 'cancelled' ? 'bg-red-500 text-white' : 'bg-white text-gray-500 border border-gray-200'}`}>ยกเลิก ({counts.cancelled})</button>
          </div>

          {/* รายการงานประวัติ */}
          <div className="px-4 mt-2 space-y-3">
            {loadingQueue ? (
              <div className="text-center text-gray-500 dark:text-gray-400 mt-10">กำลังโหลด...</div>
            ) : filteredJobs.length === 0 ? (
              <div className="text-center text-gray-400 dark:text-gray-500 mt-10">ไม่มีข้อมูลงานในหมวดหมู่นี้</div>
            ) : (
              filteredJobs.map((job) => (
                <div key={job.id} className="bg-white dark:bg-gray-700 p-4 rounded-xl shadow-sm border border-gray-100 dark:border-gray-600 flex justify-between items-center transition-colors">
                  <div className="flex-1 truncate pr-3">
                    <h3 className="font-bold text-gray-800 dark:text-white truncate">{job.customerName}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{job.problem}</p>
                    {user?.role === 'owner' && (
                      <p className="text-[10px] text-blue-500 dark:text-blue-400 font-medium mt-1">ช่าง: {job.technicianName || 'ไม่ระบุ'}</p>
                    )}
                    
                    {user?.role === 'owner' && (
                      <p className="text-xs font-bold text-gray-700 dark:text-gray-200 mt-1">฿{job.price}</p>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    {getStatusBadge(job.status)}
                    <p className="text-[10px] text-gray-400 dark:text-gray-400 mt-2">
                      {new Date(job.createdAt).toLocaleDateString('th-TH')}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>

        </div>

        {/* Bottom Navigation */}
        <div className="absolute bottom-0 w-full bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex justify-around py-3 pb-6 px-2 z-20 transition-colors duration-300">
          <button onClick={() => setActiveTab('queue')} className="flex flex-col items-center text-gray-400 dark:text-gray-500 font-medium"><span className="text-2xl mb-1">🏠</span><span className="text-xs">คิวงาน</span></button>
          <button onClick={() => setActiveTab('history')} className="flex flex-col items-center text-blue-600 font-bold"><span className="text-2xl mb-1">📊</span><span className="text-xs">ประวัติ</span></button>
          <button onClick={() => setActiveTab('profile')} className="flex flex-col items-center text-gray-400 dark:text-gray-500 font-medium"><span className="text-2xl mb-1">👤</span><span className="text-xs">โปรไฟล์</span></button>
        </div>
      </div>
    </div>
  );
}